<template>
  <Page :title="item ? 'Rechnung ' + item.number : 'Rechnung'" color="info">
    <template slot="help">
      Hier siehst du Details zu deiner Kursrechnung und kannst diese Bearbeiten,
      Löschen oder Ausdrucken.
    </template>
    <template slot="extension">
      <v-tabs background-color="info darken-1" hide-slider>
        <v-toolbar color="info darken-1" dense>
          <v-btn text exact :to="{ name: page }"
            ><v-icon left>mdi-arrow-left</v-icon>zurück</v-btn
          >
        </v-toolbar>
      </v-tabs>
    </template>

    <v-card class="mb-4" :loading="loading" v-if="item.id">
      <v-system-bar> {{ item.number }} – {{ item.description }} </v-system-bar>
      <v-divider />
      <InvoiceTable :items="item.invoices" page="InvoiceDepartment" disabled />
    </v-card>

    <v-card class="mb-4">
      <v-system-bar>Fächer</v-system-bar>
      <div class="d-flex align-center mx-4">
        <v-chip-group
          multiple
          column
          return-object
          active-class="primary--text"
          v-model="selectedSubjects"
          :disabled="coursesLoaded"
        >
          <v-tooltip top v-for="subject in subjects" :key="subject.id">
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                v-bind="attrs"
                v-on="on"
                :value="subject"
                :disabled="coursesLoaded"
                >{{ subject.code }}</v-chip
              >
            </template>
            <span>{{ subject.description }}</span>
          </v-tooltip>
        </v-chip-group>
      </div>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          text
          color="success"
          v-if="!coursesLoaded"
          :disabled="selectedSubjects.length === 0"
          @click="fetchCourses()"
          >Kurse für Fächer laden</v-btn
        >
        <v-btn v-else text color="danger" @click="coursesLoaded = false"
          >Kurse anpassen</v-btn
        >
      </v-card-actions>
    </v-card>

    <v-card class="mb-4" :loading="loading">
      <v-system-bar>Kurse</v-system-bar>

      <v-list>
        <template
          v-for="subject in selectedSubjects.filter(
            (s) => s.courses.length > 0
          )"
        >
          <v-list-item :key="'item' + subject.id">
            <v-list-item-content>
              <v-list-item-subtitle
                >{{ subject.code }} –
                {{ subject.description }}</v-list-item-subtitle
              >
              <template v-if="subject.courses.length > 8">
                <div
                  v-for="grade in getGrades(subject.courses)"
                  :key="grade"
                  class="d-flex align-center mx-4"
                >
                  <v-switch
                    inset
                    hide-details
                    class="pa-0 ma-0"
                    @change="onGradeChange($event, grade, subject)"
                    :disabled="peopleLoaded"
                  />
                  <v-chip-group
                    multiple
                    column
                    return-object
                    active-class="primary--text"
                    v-model="subject.selectedCourses"
                    :disabled="peopleLoaded"
                  >
                    <v-chip
                      :value="course"
                      v-for="course in subject.courses.filter((el) =>
                        el.code.includes(grade)
                      )"
                      :disabled="peopleLoaded"
                      :key="course.id"
                      >{{ course.code }}</v-chip
                    >
                  </v-chip-group>
                </div>
              </template>

              <template v-else>
                <div class="d-flex align-center mx-4">
                  <v-switch
                    inset
                    hide-details
                    class="pa-0 ma-0"
                    @change="onGradeChange($event, null, subject)"
                    :disabled="peopleLoaded"
                  />
                  <v-chip-group
                    multiple
                    column
                    return-object
                    active-class="primary--text"
                    v-model="subject.selectedCourses"
                    :disabled="peopleLoaded"
                  >
                    <v-chip
                      :value="course"
                      v-for="course in subject.courses"
                      :disabled="peopleLoaded"
                      :key="course.id"
                      >{{ course.code }}</v-chip
                    >
                  </v-chip-group>
                </div>
              </template>
            </v-list-item-content>
            <v-list-item-action>
              <v-text-field
                v-model="subject.weight"
                outlined
                type="number"
                label="Gewichtung"
                dense
                hide-details
                style="max-width: 100px"
              ></v-text-field>
            </v-list-item-action>
          </v-list-item>

          <v-divider :key="subject.id" />
        </template>
      </v-list>

      <v-card-actions>
        <v-spacer></v-spacer>

        <v-btn
          text
          v-if="!peopleLoaded"
          color="success"
          :disabled="selectedCourses.length === 0"
          @click="fetchPeople()"
          >Personen von Kursen laden</v-btn
        >
        <v-btn v-else text color="danger" @click="peopleLoaded = false"
          >Personen anpassen</v-btn
        >
      </v-card-actions>
    </v-card>

    <v-card class="mb-4" :loading="loadingPeople">
      <v-system-bar>Personen</v-system-bar>

      <v-toolbar flat class="mb-2">
        {{ students.length }} Schüler:innen in
        {{ selectedCourses.length }} Kursen ausgewählt
        <v-spacer />
        <v-btn
          text
          color="success"
          @click="distributeAmount"
          :disabled="loadingPeople"
        >
          verteilen
        </v-btn>
        <CurrencyValue :value="amount" />
      </v-toolbar>

      <v-expansion-panels flat accordion focusable>
        <v-expansion-panel v-for="course in selectedCourses" :key="course.id">
          <v-expansion-panel-header class="py-0 my-0">
            <v-row align="center" class="flex-nowrap ml-n8">
              <v-col cols="5">
                <CourseItem disabled :value="course"></CourseItem>
              </v-col>
              <v-col cols="2">
                <PersonChip
                  v-for="teacher in course.teachers"
                  :key="teacher.id"
                  :value="teacher"
                  codeOnly
                />
              </v-col>

              <v-col cols="1">
                <v-text-field
                  v-model="course.weight"
                  outlined
                  type="number"
                  label="Gewichtung"
                  dense
                  hide-details
                  style="max-width: 100px"
                ></v-text-field>
              </v-col>
              <v-col cols="2">
                <CurrencyValue :value="getAmount(course)" />
              </v-col>
              <v-col cols="2" class="text-center">
                <v-chip
                  outlined
                  v-if="course.students && course.students.length > 0"
                >
                  {{ course.students.length }}/{{
                    course.studentCount.total
                  }}</v-chip
                >
              </v-col>
            </v-row>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row
              no-gutters
              v-for="student in course.students"
              :key="student.id"
              align="center"
            >
              <v-col cols="7">
                <PersonItemBasic :value="student"></PersonItemBasic>
              </v-col>

              <v-col cols="3">
                <CurrencyInput v-model="student.amount" />
              </v-col>

              <v-col cols="2" class="text-right">
                <v-btn
                  class="m-auto"
                  icon
                  color="danger"
                  @click="
                    course.students = course.students.filter(
                      (s) => s.id !== student.id
                    )
                  "
                  ><v-icon>mdi-trash-can</v-icon></v-btn
                >
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card>

    <v-card>
      <v-system-bar> Speichern</v-system-bar>
      <v-card-text>
        <v-row>
          <v-col>
            <div class="d-flex align-center">
              <CurrencyValue v-model="amount" /><span class="ml-2"
                >Gesamtbetrag, davon</span
              >
            </div>

            <div class="d-flex align-center">
              <CurrencyValue v-model="amountStudents" />
              <span class="ml-2">auf Schüler:innen verteilt</span>
            </div>
          </v-col>
          <v-col>
            <v-alert v-if="check" type="error" text
              >Rundungsdifferenz zu gross!</v-alert
            >
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          text
          color="success"
          @click="save"
          :disabled="loadingPeople || check"
          :loading="saving"
        >
          Änderungen speichern
        </v-btn>
      </v-card-actions>
    </v-card>
  </Page>
</template>
<script>
import { defineComponent } from "vue";
import CurrencyValue from "common/components/CurrencyValue.vue";
import CurrencyInput from "common/components/CurrencyInput.vue";
import DepartmentInvoiceTable from "./Components/DepartmentInvoiceTable.vue";
import FinanceInvoiceForm from "common/components/Finance/FinanceInvoiceForm.vue";
import FinanceInvoiceDetailForm from "common/components/Finance/FinanceInvoiceDetailForm.vue";
import InvoiceTable from "./Components/InvoiceTable.vue";
import CourseItem from "@/components/CourseItem.vue";
import PersonItemBasic from "common/components/PersonItemBasic.vue";
import PersonChip from "common/components/PersonChip.vue";

export default defineComponent({
  components: {
    DepartmentInvoiceTable,
    InvoiceTable,
    CurrencyInput,
    CurrencyValue,
    FinanceInvoiceForm,
    FinanceInvoiceDetailForm,
    CourseItem,
    PersonItemBasic,
    PersonChip,
  },
  props: ["id", "page"],
  data() {
    return {
      loading: false,
      loadingPeople: false,
      saving: false,
      item: { id: 0, invoices: [] },
      selectedSubjects: [],
      subjects: [],
      coursesLoaded: false,
      peopleLoaded: false,
    };
  },

  methods: {
    async fetchSubjects() {
      this.subjects = (
        await this.apiList({
          resource: "common/subject",
          query: "department=BG",
        })
      ).map((subject) => {
        return {
          ...subject,
          courses: [],
          selectedCourses: [],
          weight: 1,
        };
      });
    },
    async fetchCourses() {
      if (this.selectedSubjects.some((subject) => subject.courses.length > 0)) {
        if (
          !(await this.$root.confirm({
            message:
              "Sollen die Kurse neu geladen werden? Die bestehenden Kurse und Schüler:innen werden dabei gelöscht.",
            color: "danger",
          }))
        ) {
          return;
        }
      }
      this.loading = true;
      for (const subject of this.subjects) {
        subject.selectedCourses = [];
      }
      for (const subject of this.selectedSubjects) {
        subject.courses = (
          await this.apiList({
            resource: "course/coursepart",
            query:
              "subject=" +
              subject.code +
              "&startDate=" +
              this.term.startDate +
              "&endDate=" +
              this.term.endDate,
          })
        ).map((course) => {
          return {
            ...course,
            students: [],
            weight: 1,
          };
        });
      }

      this.loading = false;
      this.coursesLoaded = true;
      this.peopleLoaded = false;
    },
    async fetchData() {
      this.loading = true;
      this.item = await this.apiGet({
        resource: "finance/invoicegroup",
        id: this.id,
      });
      this.term = await this.apiList({
        resource: "common/term",
        query: "current",
      });
      await this.fetchSubjects();
      this.loading = false;
    },

    async fetchPeople() {
      if (this.students.length > 0) {
        if (
          !(await this.$root.confirm({
            message:
              "Sollen die Personen neu geladen werden? Die bestehenden Gewichte werden dabei ersetzt.",
            color: "danger",
          }))
        ) {
          return;
        }
      }
      this.loadingPeople = true;
      for (const subject of this.selectedSubjects) {
        for (const course of subject.selectedCourses) {
          course.weight = subject.weight;
          course.students = (
            await this.apiList({
              resource: "person/person",
              query: `course=${course.id}`,
            })
          ).map((person) => {
            return {
              ...person,
              amount: 0,
            };
          });
        }
      }
      this.loadingPeople = false;
      this.peopleLoaded = true;
    },
    distributeAmount() {
      const totalWeight = this.selectedCourses.reduce(
        (sum, course) => sum + course.weight * course.students.length,
        0
      );
      const amount = this.amount / totalWeight;
      this.selectedCourses.forEach((course) => {
        course.students.forEach((student) => {
          student.amount = Math.floor(100 * course.weight * amount) / 100;
        });
      });
    },
    getGrades(courses) {
      return [
        ...new Set(courses.map((course) => course.code.match(/\d+/g)).flat()),
      ].sort((a, b) => a.localeCompare(b));
    },
    getAmount(course) {
      return course.students.reduce((sum, student) => sum + student.amount, 0);
    },
    onGradeChange(checked, grade, subject) {
      if (checked) {
        subject.selectedCourses.push(
          ...subject.courses.filter(
            (course) =>
              grade === null ||
              (course.code.includes(grade) &&
                !subject.selectedCourses.includes(course))
          )
        );
      } else {
        subject.selectedCourses = subject.selectedCourses.filter(
          (course) => !course.code.includes(grade) && grade != null
        );
      }
    },
    async save() {
      this.saving = true;

      await this.apiPut({
        resource: "finance/invoicegroup",
        id: this.item.id,
        data: {
          id: this.item.id,
          details: this.details,
        },
        onSuccess: () => {
          this.$router.push({ name: "InvoiceDepartment" });
        },
      });
      this.saving = false;
    },
  },
  computed: {
    amount() {
      return this.item.invoices.reduce(
        (sum, invoice) => sum + invoice.amount,
        0
      );
    },
    amountStudents() {
      return this.students.reduce((sum, student) => sum + student.amount, 0);
    },
    selectedCourses() {
      return this.selectedSubjects
        .map((subject) => subject.selectedCourses)
        .flat();
    },
    students() {
      return this.selectedCourses.map((course) => course.students).flat();
    },

    details() {
      const totalsByPerson = new Map();

      this.students.forEach((student) => {
        const currentAmount = totalsByPerson.get(student.id) || 0;
        totalsByPerson.set(student.id, currentAmount + Number(student.amount));
      });

      return Array.from(totalsByPerson, ([person, amount]) => ({
        person,
        amount,
      }));
    },
    difference() {
      return this.amount - this.amountStudents;
    },
    check() {
      return Math.abs(this.difference) > this.tolerance;
    },
    tolerance() {
      return 0.02 * this.students.length;
    },
  },
  watch: {
    id() {
      this.fetchData();
    },
  },
  async created() {
    this.fetchData();
  },
});
</script>
